<template>
  <div
    v-if="shouldShow"
    class="payment-holiday-modal-wrapper"
    @click.self="close"
    @keydown.esc="close"
    role="dialog"
    aria-modal="true"
    aria-labelledby="payment-holiday-modal-title"
  >
    <div class="payment-holiday-modal" ref="modalContent">
      <div
        class="payment-holiday-modal__close"
        @click="close"
        @keydown.enter="close"
        @keydown.space.prevent="close"
        role="button"
        tabindex="0"
        aria-label="Fechar modal de aviso de pagamentos"
      >
        <v-icon name="close" aria-hidden="true"></v-icon>
      </div>

      <div class="payment-holiday-modal__content">
        <div class="payment-holiday-modal__icon" aria-hidden="true">⚠️</div>
        <div
          id="payment-holiday-modal-title"
          class="payment-holiday-modal__title"
        >
          Aviso importante – Semana de Pagamentos
        </div>

        <div class="payment-holiday-modal__text">
          <div class="payment-holiday-modal__section">
            <div class="payment-holiday-modal__section-icon" aria-hidden="true">💰</div>
            <div class="payment-holiday-modal__section-title">Pagamentos na Vibbra</div>
            <p>Devido ao feriado do dia 20/11 (quinta-feira), os bancos não realizarão processamento de transferências nesta data.</p>
          </div>

          <div class="payment-holiday-modal__section">
            <div class="payment-holiday-modal__section-icon" aria-hidden="true">✅</div>
            <p>As solicitações de resgate feitas até 19/11 serão processadas normalmente no próximo dia útil, 21/11 (sexta-feira).</p>
          </div>

          <p class="payment-holiday-modal__footer">Agradecemos pela compreensão e atenção!</p>
        </div>

        <div class="payment-holiday-modal__button">
          <v-button @click="close" class="primary">
            Entendi
          </v-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { safeLocalStorage } from '@/utils/storage';

export default {
  name: 'PaymentHolidayNoticeModal',
  data() {
    return {
      // ID mais específico para evitar colisões
      campaignId: 'VIBBRA_PAYMENT_HOLIDAY_NOV20_2024',
      shouldShow: false,
      previousFocusedElement: null
    }
  },
  methods: {
    /**
     * Fecha a modal, salva no localStorage e restaura foco
     */
    close() {
      this.shouldShow = false;
      safeLocalStorage.setItem(this.campaignId, 'closed');
      this.$emit('onHide');

      // Restaurar foco ao elemento anterior
      if (this.previousFocusedElement) {
        this.$nextTick(() => {
          if (this.previousFocusedElement && typeof this.previousFocusedElement.focus === 'function') {
            this.previousFocusedElement.focus();
          }
        });
      }
    },

    /**
     * Verifica se a data atual está dentro do período de exibição
     * @returns {boolean} - true se está no período (19-21/nov/2024)
     */
    isWithinNoticePeriod() {
      const now = new Date();

      // Hardcode do ano para garantir que funciona apenas em 2024
      const year = 2024;

      // Período: 19/11/2024 às 00:00 até 21/11/2024 às 23:59:59
      const startDate = new Date(year, 10, 19, 0, 0, 0); // Mês 10 = Novembro (0-indexed)
      const endDate = new Date(year, 10, 21, 23, 59, 59);

      return now >= startDate && now <= endDate;
    },

    /**
     * Verifica se a modal deve ser exibida
     * Considera: período correto + não foi fechada anteriormente
     */
    checkIfShouldShow() {
      // Verifica se está no período correto
      if (!this.isWithinNoticePeriod()) {
        this.shouldShow = false;
        return;
      }

      // Verifica se o modal já foi fechado anteriormente
      const wasClosed = safeLocalStorage.getItem(this.campaignId);
      this.shouldShow = !wasClosed;
    },

    /**
     * Implementa trap de foco para manter usuário dentro da modal
     */
    setupFocusTrap() {
      if (!this.$refs.modalContent) return;

      const focusableElements = this.$refs.modalContent.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Handler para trap de foco
      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      this.$refs.modalContent.addEventListener('keydown', this.focusTrapHandler);
    },

    /**
     * Remove trap de foco
     */
    removeFocusTrap() {
      if (this.$refs.modalContent && this.focusTrapHandler) {
        this.$refs.modalContent.removeEventListener('keydown', this.focusTrapHandler);
      }
    }
  },
  mounted() {
    this.checkIfShouldShow();

    if (this.shouldShow) {
      // Salvar elemento com foco atual
      this.previousFocusedElement = document.activeElement;

      // Focar no botão de fechar quando a modal aparecer
      this.$nextTick(() => {
        const closeButton = this.$el.querySelector('.payment-holiday-modal__close');
        if (closeButton) {
          closeButton.focus();
        }

        // Configurar trap de foco
        this.setupFocusTrap();
      });
    }
  },
  beforeDestroy() {
    // Limpar trap de foco ao destruir componente
    this.removeFocusTrap();
  }
}
</script>

<style lang="scss" scoped>
.payment-holiday-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: var(--modal-z-index, 9999);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.payment-holiday-modal {
  background: white;
  border-radius: 12px;
  max-width: 550px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.payment-holiday-modal__close {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  color: #999;
  transition: color 0.2s;
  z-index: 1;

  // Melhorar área de clique
  padding: 5px;

  // Indicador visual de foco para acessibilidade
  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:hover {
    color: #333;
  }
}

.payment-holiday-modal__content {
  padding: 40px 30px 30px;
  text-align: center;
}

.payment-holiday-modal__icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.payment-holiday-modal__title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 25px;
  line-height: 1.4;
}

.payment-holiday-modal__text {
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 25px;
  text-align: left;

  p {
    margin-bottom: 10px;
    line-height: 1.6;
  }
}

.payment-holiday-modal__section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;

  p {
    margin: 0;
  }
}

.payment-holiday-modal__section-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.payment-holiday-modal__section-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.payment-holiday-modal__footer {
  text-align: center;
  font-style: italic;
  color: #666;
  margin-top: 15px;
  margin-bottom: 0;
}

.payment-holiday-modal__button {
  margin-top: 20px;

  .primary {
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;
    font-weight: 600;

    // Indicador visual de foco
    &:focus {
      outline: 2px solid #007bff;
      outline-offset: 2px;
    }
  }
}

// Responsividade para mobile
@media (max-width: 576px) {
  .payment-holiday-modal-wrapper {
    padding: 15px;
  }

  .payment-holiday-modal {
    max-width: 100%;
  }

  .payment-holiday-modal__content {
    padding: 35px 20px 25px;
  }

  .payment-holiday-modal__icon {
    font-size: 40px;
  }

  .payment-holiday-modal__title {
    font-size: 18px;
    margin-bottom: 20px;
  }

  .payment-holiday-modal__text {
    font-size: 15px;
  }

  .payment-holiday-modal__section {
    padding: 12px;
  }

  .payment-holiday-modal__section-icon {
    font-size: 20px;
  }

  .payment-holiday-modal__section-title {
    font-size: 16px;
  }
}

// Responsividade para tablets
@media (min-width: 577px) and (max-width: 768px) {
  .payment-holiday-modal {
    max-width: 500px;
  }

  .payment-holiday-modal__icon {
    font-size: 44px;
  }

  .payment-holiday-modal__title {
    font-size: 19px;
  }
}
</style>



// WEBPACK FOOTER //
// src/parts/modals/payment-holiday-notice-modal.vue